import settings from '@/data/constants';
import { QueryKey } from '@/data/query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { apiV1Contract } from '@montugroup/pms-api-contracts';
import axios from 'axios';

import { toast } from '@montugroup/design-system';

export interface DassAssessment {
  id: number;
  patient_id: number;
  uuid: string;
  link_send_date: string;
  link_send_count: number;
  days_link_valid: number;
  is_submitted: boolean;
  submitted_date: string;
}

export interface DassAssessmentDetails {
  id: number;
  patient_id: number;
  uuid: string;
  link_send_date: string;
  link_send_count: number;
  days_link_valid: number;
  is_submitted: boolean;
  submitted_date: string;
  valid_hours: number;
  elapsed_hours: string;
  Patient?: {
    id: number;
    user_id: number;
    PatientUser: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
  };
}

export interface DassAssessmentDetails {
  id: number;
  patient_id: number;
  uuid: string;
  link_send_date: string;
  link_send_count: number;
  days_link_valid: number;
  is_submitted: boolean;
  submitted_date: string;
  valid_hours: number;
  elapsed_hours: string;
  Patient?: {
    id: number;
    user_id: number;
    PatientUser: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
  };
}

type DassScoreKeys = 'anxiety' | 'depression' | 'stress';

type DassAssessmentScores = {
  [DassScoreKey in DassScoreKeys]: {
    score: number;
    level: string;
  };
};

export const useDassAssessment = ({ patientId }: { patientId: number }) => {
  const { data, refetch } = useQuery<DassAssessment>({
    queryKey: [QueryKey.GetDassAssessment, String(patientId)],
    queryFn: async () => {
      const response = await axios.get(`${settings.url}/dass/getDass?patientId=${patientId}`);
      return response.data;
    },
    enabled: Boolean(patientId)
  });

  return {
    data,
    refetch
  };
};

export const useDassAssessmentDetails = ({
  patientId,
  dassAssessmentUuid
}: {
  patientId: number;
  dassAssessmentUuid: string;
}) => {
  const emptyDassAssessmentDetails: DassAssessmentDetails = {
    id: 0,
    patient_id: patientId,
    uuid: '',
    link_send_date: '',
    link_send_count: 0,
    days_link_valid: 0,
    is_submitted: false,
    submitted_date: '',
    valid_hours: 0,
    elapsed_hours: ''
  };

  const { data, refetch } = useQuery<DassAssessmentDetails>({
    queryKey: [QueryKey.GetDassValidity, String(patientId)],
    queryFn: async () => {
      const response = await axios.get(
        `${settings.url}/dass/patientDassValidity?patientId=${patientId}&uuid=${dassAssessmentUuid}`
      );
      return response.data;
    },
    enabled: Boolean(patientId) && Boolean(dassAssessmentUuid)
  });

  return {
    data: data ?? emptyDassAssessmentDetails,
    refetch
  };
};

export const useDassAssessmentScores = ({ patientId, enabled }: { patientId: number; enabled: boolean }) => {
  const emptyDassAssessmentScores: DassAssessmentScores = {
    anxiety: {
      score: 0,
      level: ''
    },
    depression: {
      score: 0,
      level: ''
    },
    stress: {
      score: 0,
      level: ''
    }
  };

  const { data, refetch, isLoading } = useQuery<DassAssessmentScores>({
    queryKey: [QueryKey.GetDassAssessmentScores, String(patientId)],
    queryFn: async () => {
      const response = await axios.get(`${settings.url}/dass/dassAssessmentCalculation?patientId=${patientId}`);
      return response.data;
    },
    enabled: Boolean(patientId) && Boolean(enabled)
  });

  return {
    data: data ?? emptyDassAssessmentScores,
    isLoading,
    refetch
  };
};

type CreateDassParams = Zod.infer<typeof apiV1Contract.dass.createDass.body>;
export const useCreateDassAssessment = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation({
    mutationFn: async (payload: CreateDassParams) => {
      const response = await axios.post(`${settings.url}/dass/createDass`, payload);
      return response.data;
    },
    mutationKey: [QueryKey.CreateDassAssessment],
    onSuccess: (_data, params) => {
      toast.success('Sent Dass Assessment Email');
      queryClient.invalidateQueries({ queryKey: [QueryKey.GetDassAssessment, String(params.patientId)] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.GetDassValidity, String(params.patientId)] });
    },
    onError: () => {
      toast.error('Failed to Sent Dass Assessment Email');
    }
  });

  return {
    createDassAssessment: mutateAsync,
    ...rest
  };
};

type DeactivateDassParams = Zod.infer<typeof apiV1Contract.dass.deactivateDass.body>;
export const useDeactivateDass = () => {
  const { mutateAsync, ...rest } = useMutation({
    mutationFn: async (payload: DeactivateDassParams) => {
      const response = await axios.put(`${settings.url}/dass/deactivateDass`, payload);
      return response.data;
    },
    mutationKey: [QueryKey.DeactivateDass]
  });

  return {
    deactivateDass: mutateAsync,
    ...rest
  };
};
